// "Update available" card (IMP-064). lib/appUpdate.ts shows it when a new
// version is ready while someone is using the app; the silent paths (at
// launch, and at the start of a session with nothing being edited) never
// render it. Mounted once in AppShell.
//
// Design: docs/mockups/update-notification-mockup.html — a dark card above the
// bottom navigation on phones, centred at the bottom of the page area beside
// the sidebar on desktop (`.update-card-dock` in index.css). Modelled on Home
// Assistant's frontend update toast, without its auto-reload countdown.

import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

import {
  applyUpdate,
  dismissUpdate,
  getAppUpdateState,
  subscribeAppUpdate,
  type UpdateState,
} from '../lib/appUpdate';
import { confirmDialog } from '../lib/confirm';
import { hasUnsavedChanges } from '../lib/unsavedChanges';

export function UpdateCard() {
  const [update, setUpdate] = useState<UpdateState>(getAppUpdateState);

  // subscribeAppUpdate returns its unsubscribe function, which is what
  // useEffect wants back.
  useEffect(() => subscribeAppUpdate(setUpdate), []);

  if (update.status === 'none' || update.dismissed) return null;
  const reloading = update.status === 'reloading';

  const reload = async () => {
    // A reload clears whatever is on screen: never do that to unsaved work
    // without asking.
    if (hasUnsavedChanges()) {
      const confirmed = await confirmDialog({
        title: 'Discard unsaved changes?',
        message:
          "Reloading applies the new version and clears anything you haven't saved on this page.",
        confirmLabel: 'Reload anyway',
        cancelLabel: 'Keep editing',
      });
      if (!confirmed) return;
    }
    applyUpdate();
  };

  return (
    // Polite live region: announced without stealing focus. Hidden when
    // printing (the purchase-order sheet prints from the browser).
    <div className="update-card-dock print:hidden" role="status" aria-live="polite">
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2.5 rounded-xl bg-carbon py-3 pl-3.5 pr-3 text-white shadow-e3 animate-fade-in md:grid-cols-[auto_1fr_auto]">
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10"
          aria-hidden
        >
          <RefreshCw size={16} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug">Update available</p>
          <p className="mt-0.5 text-xs leading-snug text-white/80">
            A new version of OnLogic Team is ready.
          </p>
        </div>
        {/* Buttons wrap under the text on phones, sit beside it on desktop. */}
        <div className="col-span-2 flex justify-end gap-2 md:col-span-1">
          <button
            type="button"
            onClick={dismissUpdate}
            disabled={reloading}
            className="h-10 rounded-lg px-3.5 text-sm font-medium text-white/90 transition-colors duration-base hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange disabled:opacity-50"
          >
            Later
          </button>
          <button
            type="button"
            onClick={() => void reload()}
            disabled={reloading}
            className="btn-primary h-10"
          >
            {reloading ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden />
                Reloading…
              </>
            ) : (
              'Reload'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
